import { autorun } from 'mobx';
import { cast, types, addDisposer } from 'mobx-state-tree';
import { hierarchy, cluster } from 'd3-hierarchy';
import { ascending } from 'd3-array';
import { FileLocation, ElementId } from '@jbrowse/core/util/types/mst';
import { openLocation } from '@jbrowse/core/util/io';
import { measureText, sum } from '@jbrowse/core/util';
import BaseViewModel from '@jbrowse/core/pluggableElementTypes/models/BaseViewModel';
import Stockholm from 'stockholm-js';
// locals
import { collapse, generateNodeIds, maxLength, setBrLength, skipBlanks, clamp, } from './util';
import TextTrack from './components/TextTrack';
import BoxTrack from './components/BoxTrack';
import ClustalMSA from './parsers/ClustalMSA';
import StockholmMSA from './parsers/StockholmMSA';
import FastaMSA from './parsers/FastaMSA';
import parseNewick from './parseNewick';
import colorSchemes from './colorSchemes';
import { UniprotTrack } from './UniprotTrack';
import { StructureModel } from './StructureModel';
import { DialogQueueSessionMixin } from './DialogQueue';
/**
 * #stateModel MsaView
 */
function x() { } // eslint-disable-line @typescript-eslint/no-unused-vars
const model = types
    .compose(BaseViewModel, DialogQueueSessionMixin(), types.model('MsaView', {
    /**
     * #property
     */
    id: ElementId,
    /**
     * #property
     */
    type: types.literal('MsaView'),
    /**
     * #property
     */
    height: types.optional(types.number, 550),
    /**
     * #property
     */
    treeAreaWidth: types.optional(types.number, 400),
    /**
     * #property
     */
    treeWidth: types.optional(types.number, 300),
    /**
     * #getter
     */
    treeWidthMatchesArea: true,
    /**
     * #property
     */
    rowHeight: 20,
    /**
     * #property
     */
    scrollY: 0,
    /**
     * #property
     */
    scrollX: 0,
    /**
     * #property
     */
    resizeHandleWidth: 5,
    /**
     * #property
     */
    blockSize: 1000,
    /**
     * #property
     */
    mouseRow: types.maybe(types.number),
    /**
     * #property
     */
    mouseCol: types.maybe(types.number),
    /**
     * #property
     */
    selectedStructures: types.array(StructureModel),
    /**
     * #property
     */
    labelsAlignRight: false,
    /**
     * #property
     */
    colWidth: 16,
    /**
     * #property
     */
    showBranchLen: true,
    /**
     * #property
     */
    bgColor: true,
    /**
     * #property
     */
    drawTree: true,
    /**
     * #property
     */
    drawNodeBubbles: true,
    /**
     * #property
     */
    highResScaleFactor: 2,
    /**
     * #property
     */
    colorSchemeName: 'maeditor',
    /**
     * #property
     */
    treeFilehandle: types.maybe(FileLocation),
    /**
     * #property
     */
    msaFilehandle: types.maybe(FileLocation),
    /**
     * #property
     */
    treeMetadataFilehandle: types.maybe(FileLocation),
    /**
     * #property
     */
    currentAlignment: 0,
    /**
     * #property
     */
    collapsed: types.array(types.string),
    /**
     * #property
     */
    showOnly: types.maybe(types.string),
    /**
     * #property
     */
    boxTracks: types.array(UniprotTrack),
    /**
     * #property
     */
    turnedOffTracks: types.map(types.boolean),
    /**
     * #property
     */
    annotatedRegions: types.array(types.model({
        start: types.number,
        end: types.number,
        attributes: types.frozen(),
    })),
    /**
     * #property
     */
    data: types.optional(types
        .model({
        tree: types.maybe(types.string),
        msa: types.maybe(types.string),
        treeMetadata: types.maybe(types.string),
    })
        .actions(self => ({
        setTree(tree) {
            self.tree = tree;
        },
        setMSA(msa) {
            self.msa = msa;
        },
        setTreeMetadata(treeMetadata) {
            self.treeMetadata = treeMetadata;
        },
    })), { tree: '', msa: '' }),
}))
    .volatile(() => ({
    /**
     * #volatile
     * a dummy variable that is incremented when ref changes so autorun for
     * drawing canvas commands will run
     */
    nref: 0,
    /**
     * #volatile
     */
    minimapHeight: 56,
    /**
     * #volatile
     */
    error: undefined,
    /**
     * #volatile
     */
    margin: {
        left: 20,
        top: 20,
    },
    /**
     * #volatile
     */
    annotPos: undefined,
}))
    .actions(self => ({
    /**
     * #action
     */
    setHeight(height) {
        self.height = height;
    },
    /**
     * #action
     */
    addStructureToSelection(elt) {
        self.selectedStructures.push(elt);
    },
    /**
     * #action
     */
    removeStructureFromSelection(elt) {
        const r = self.selectedStructures.find(node => node.id === elt.id);
        if (r) {
            self.selectedStructures.remove(r);
        }
    },
    /**
     * #action
     */
    toggleStructureSelection(elt) {
        const r = self.selectedStructures.find(node => node.id === elt.id);
        if (r) {
            self.selectedStructures.remove(r);
        }
        else {
            self.selectedStructures.push(elt);
        }
    },
    /**
     * #action
     */
    clearSelectedStructures() {
        self.selectedStructures = cast([]);
    },
    /**
     * #action
     */
    setError(error) {
        self.error = error;
    },
    /**
     * #action
     */
    setMousePos(col, row) {
        self.mouseCol = col;
        self.mouseRow = row;
    },
    /**
     * #action
     */
    setRowHeight(n) {
        self.rowHeight = n;
    },
    /**
     * #action
     */
    setColWidth(n) {
        self.colWidth = n;
    },
    /**
     * #action
     */
    setColorSchemeName(name) {
        self.colorSchemeName = name;
    },
    /**
     * #action
     */
    setTreeWidthMatchesArea(arg) {
        self.treeWidthMatchesArea = arg;
    },
    /**
     * #action
     */
    setScrollY(n) {
        self.scrollY = n;
    },
    /**
     * #action
     */
    setTreeAreaWidth(n) {
        self.treeAreaWidth = n;
    },
    /**
     * #action
     */
    setTreeWidth(n) {
        self.treeWidth = n;
    },
    /**
     * #action
     */
    setCurrentAlignment(n) {
        self.currentAlignment = n;
    },
    /**
     * #action
     */
    setLabelsAlignRight(arg) {
        self.labelsAlignRight = arg;
    },
    /**
     * #action
     */
    setDrawTree(arg) {
        self.drawTree = arg;
    },
    /**
     * #action
     */
    toggleCollapsed(node) {
        if (self.collapsed.includes(node)) {
            self.collapsed.remove(node);
        }
        else {
            self.collapsed.push(node);
        }
    },
    /**
     * #action
     */
    setShowOnly(node) {
        self.showOnly = node;
    },
    /**
     * #action
     */
    setShowBranchLen(arg) {
        self.showBranchLen = arg;
    },
    /**
     * #action
     */
    setBgColor(arg) {
        self.bgColor = arg;
    },
    /**
     * #action
     */
    setDrawNodeBubbles(arg) {
        self.drawNodeBubbles = arg;
    },
    /**
     * #action
     */
    setData(data) {
        self.data = cast(data);
    },
    /**
     * #action
     */
    async setMSAFilehandle(msaFilehandle) {
        self.msaFilehandle = msaFilehandle;
    },
    /**
     * #action
     */
    async setTreeFilehandle(treeFilehandle) {
        if (treeFilehandle && 'blobId' in treeFilehandle) {
            const r = await openLocation(treeFilehandle).readFile('utf8');
            this.setTree(r);
        }
        else {
            self.treeFilehandle = treeFilehandle;
        }
    },
    /**
     * #action
     */
    setMSA(result) {
        self.data.setMSA(result);
    },
    /**
     * #action
     */
    setTree(result) {
        self.data.setTree(result);
    },
    /**
     * #action
     */
    setTreeMetadata(result) {
        self.data.setTreeMetadata(result);
    },
}))
    .views(self => {
    let oldBlocksX = [];
    let oldBlocksY = [];
    let oldValX = 0;
    let oldValY = 0;
    return {
        /**
         * #getter
         */
        get initialized() {
            return ((self.data.msa ||
                self.data.tree ||
                self.msaFilehandle ||
                self.treeFilehandle) &&
                !self.error);
        },
        /**
         * #getter
         */
        get blocksX() {
            const { scrollX, blockSize: size, colWidth } = self;
            const ret = -(size * Math.floor(scrollX / size)) - size;
            const b = [];
            for (let i = ret; i < ret + size * 3; i += size) {
                if (i + size > 0) {
                    b.push(i);
                }
            }
            if (JSON.stringify(b) !== JSON.stringify(oldBlocksX) ||
                colWidth !== oldValX) {
                oldBlocksX = b;
                oldValX = colWidth;
            }
            return oldBlocksX;
        },
        /**
         * #getter
         */
        get blocksY() {
            const { scrollY, blockSize: size, rowHeight } = self;
            const ret = -(size * Math.floor(scrollY / size)) - 2 * size;
            const b = [];
            for (let i = ret; i < ret + size * 3; i += size) {
                if (i + size > 0) {
                    b.push(i);
                }
            }
            if (JSON.stringify(b) !== JSON.stringify(oldBlocksY) ||
                rowHeight !== oldValY) {
                oldBlocksY = b;
                oldValY = rowHeight;
            }
            return oldBlocksY;
        },
    };
})
    .views(self => ({
    /**
     * #getter
     */
    get blocks2d() {
        return self.blocksY.flatMap(by => self.blocksX.map(bx => [bx, by]));
    },
    /**
     * #getter
     */
    get done() {
        return self.initialized && (self.data.msa || self.data.tree);
    },
    /**
     * #getter
     */
    get colorScheme() {
        return colorSchemes[self.colorSchemeName];
    },
    /**
     * #getter
     */
    get header() {
        var _a;
        return ((_a = this.MSA) === null || _a === void 0 ? void 0 : _a.getHeader()) || {};
    },
    /**
     * #method
     */
    getRowData(name) {
        var _a;
        const matches = name.match(/\S+\/(\d+)-(\d+)/);
        return {
            data: ((_a = this.MSA) === null || _a === void 0 ? void 0 : _a.getRowData(name)) || {},
            ...(matches && { range: { start: +matches[1], end: +matches[2] } }),
        };
    },
    /**
     * #getter
     */
    get currentAlignmentName() {
        return this.alignmentNames[self.currentAlignment];
    },
    /**
     * #getter
     */
    get alignmentNames() {
        var _a;
        return ((_a = this.MSA) === null || _a === void 0 ? void 0 : _a.alignmentNames) || [];
    },
    /**
     * #getter
     */
    get noTree() {
        return !!this.tree.noTree;
    },
    /**
     * #getter
     */
    get menuItems() {
        return [];
    },
    /**
     * #getter
     */
    get treeMetadata() {
        return self.data.treeMetadata ? JSON.parse(self.data.treeMetadata) : {};
    },
    /**
     * #getter
     */
    get MSA() {
        const text = self.data.msa;
        if (text) {
            if (Stockholm.sniff(text)) {
                return new StockholmMSA(text, self.currentAlignment);
            }
            else if (text.startsWith('>')) {
                return new FastaMSA(text);
            }
            else {
                return new ClustalMSA(text);
            }
        }
        return null;
    },
    /**
     * #getter
     */
    get numColumns() {
        var _a;
        return (((_a = this.MSA) === null || _a === void 0 ? void 0 : _a.getWidth()) || 0) - this.blanks.length;
    },
    /**
     * #getter
     */
    get tree() {
        var _a;
        return self.data.tree
            ? generateNodeIds(parseNewick(self.data.tree))
            : ((_a = this.MSA) === null || _a === void 0 ? void 0 : _a.getTree()) || {
                noTree: true,
                branchset: [],
                id: 'empty',
                name: 'empty',
            };
    },
    /**
     * #getter
     */
    get rowNames() {
        return this.hierarchy.leaves().map(node => node.data.name);
    },
    /**
     * #getter
     */ get mouseOverRowName() {
        return self.mouseRow !== undefined
            ? this.rowNames[self.mouseRow]
            : undefined;
    },
    /**
     * #method
     */
    getMouseOverResidue(rowName) {
        return this.columns[rowName];
    },
    /**
     * #getter
     */
    get root() {
        let hier = hierarchy(this.tree, d => d.branchset)
            .sum(d => (d.branchset ? 0 : 1))
            .sort((a, b) => ascending(a.data.length || 1, b.data.length || 1));
        if (self.showOnly) {
            const res = hier.find(node => node.data.id === self.showOnly);
            if (res) {
                hier = res;
            }
        }
        if (self.collapsed.length) {
            self.collapsed
                .map(collapsedId => hier.find(node => node.data.id === collapsedId))
                .filter((f) => !!f)
                .map(node => collapse(node));
        }
        return hier;
    },
    /**
     * #getter
     */
    get structures() {
        var _a;
        return ((_a = this.MSA) === null || _a === void 0 ? void 0 : _a.getStructures()) || {};
    },
    /**
     * #getter
     */
    get inverseStructures() {
        return Object.fromEntries(Object.entries(this.structures).flatMap(([key, val]) => val.map(pdbEntry => [pdbEntry.pdb, { id: key }])));
    },
    /**
     * #getter
     */
    get msaAreaWidth() {
        return self.width - self.treeAreaWidth;
    },
    /**
     * #getter
     */
    get blanks() {
        var _a;
        const blanks = [];
        const strs = this.hierarchy
            .leaves()
            .map(({ data }) => { var _a; return (_a = this.MSA) === null || _a === void 0 ? void 0 : _a.getRow(data.name); })
            .filter((item) => !!item);
        for (let i = 0; i < ((_a = strs[0]) === null || _a === void 0 ? void 0 : _a.length); i++) {
            let counter = 0;
            for (const str of strs) {
                if (str[i] === '-') {
                    counter++;
                }
            }
            if (counter === strs.length) {
                blanks.push(i);
            }
        }
        return blanks;
    },
    /**
     * #getter
     */
    get rows() {
        return this.hierarchy
            .leaves()
            .map(({ data }) => { var _a; return [data.name, (_a = this.MSA) === null || _a === void 0 ? void 0 : _a.getRow(data.name)]; })
            .filter((f) => !!f[1]);
    },
    /**
     * #getter
     */
    get columns() {
        return Object.fromEntries(this.rows.map((row, index) => [row[0], this.columns2d[index]]));
    },
    /**
     * #getter
     */
    get columns2d() {
        return this.rows.map(r => r[1]).map(str => skipBlanks(this.blanks, str));
    },
    /**
     * #getter
     */
    get fontSize() {
        return Math.max(8, self.rowHeight - 8);
    },
    /**
     * #getter
     */
    get colStats() {
        const r = [];
        const columns = this.columns2d;
        for (const column of columns) {
            for (let j = 0; j < column.length; j++) {
                const l = r[j] || {};
                if (!l[column[j]]) {
                    l[column[j]] = 0;
                }
                l[column[j]]++;
                r[j] = l;
            }
        }
        return r;
    },
    /**
     * #getter
     * generates a new tree that is clustered with x,y positions
     */
    get hierarchy() {
        const root = this.root;
        const clust = cluster()
            .size([this.totalHeight, self.treeWidth])
            .separation(() => 1);
        clust(root);
        setBrLength(root, (root.data.length = 0), self.treeWidth / maxLength(root));
        return root;
    },
    /**
     * #getter
     */
    get totalHeight() {
        return this.root.leaves().length * self.rowHeight;
    },
}))
    .actions(self => ({
    /**
     * #action
     */
    addUniprotTrack(node) {
        if (self.boxTracks.some(t => t.name === node.name)) {
            if (self.turnedOffTracks.has(node.name)) {
                this.toggleTrack(node.name);
            }
        }
        else {
            self.boxTracks.push({
                ...node,
                id: node.name,
                associatedRowName: node.name,
            });
        }
    },
    /**
     * #action
     */
    doScrollY(deltaY) {
        self.scrollY = clamp(-self.totalHeight + 10, self.scrollY + deltaY, 0);
    },
    /**
     * #action
     */
    doScrollX(deltaX) {
        self.scrollX = clamp(-(self.numColumns * self.colWidth) + (self.msaAreaWidth - 100), self.scrollX + deltaX, 0);
    },
    /**
     * #action
     */
    setScrollX(n) {
        self.scrollX = clamp(-(self.numColumns * self.colWidth) + (self.msaAreaWidth - 100), n, 0);
    },
    /**
     * #action
     */
    setMouseoveredColumn(n, chain, file) {
        var _a;
        let j = 0;
        let i = 0;
        const { id } = self.inverseStructures[file.slice(0, -4)] || {};
        const row = (_a = self.MSA) === null || _a === void 0 ? void 0 : _a.getRow(id);
        if (row) {
            for (i = 0; i < row.length && j < n; i++) {
                if (row[i] !== '-') {
                    j++;
                }
            }
            self.mouseCol = j + 1;
        }
        else {
            self.mouseCol = undefined;
        }
    },
    /**
     * #action
     */
    toggleTrack(id) {
        if (self.turnedOffTracks.has(id)) {
            self.turnedOffTracks.delete(id);
        }
        else {
            self.turnedOffTracks.set(id, true);
        }
    },
}))
    .views(self => ({
    /**
     * #getter
     */
    get labelsWidth() {
        var _a;
        let x = 0;
        const { rowHeight, hierarchy, treeMetadata, fontSize } = self;
        if (rowHeight > 5) {
            for (const node of hierarchy.leaves()) {
                const { data: { name }, } = node;
                const displayName = ((_a = treeMetadata[name]) === null || _a === void 0 ? void 0 : _a.genome) || name;
                x = Math.max(measureText(displayName, fontSize), x);
            }
        }
        return x;
    },
    /**
     * #getter
     */
    get secondaryStructureConsensus() {
        var _a;
        return (_a = self.MSA) === null || _a === void 0 ? void 0 : _a.secondaryStructureConsensus;
    },
    /**
     * #getter
     */
    get seqConsensus() {
        var _a;
        return (_a = self.MSA) === null || _a === void 0 ? void 0 : _a.seqConsensus;
    },
    /**
     * #getter
     */
    get conservation() {
        if (self.columns2d.length) {
            for (let i = 0; i < self.columns2d[0].length; i++) {
                const col = [];
                for (const column of self.columns2d) {
                    col.push(column[i]);
                }
            }
        }
        return ['a'];
    },
    /**
     * #getter
     */
    get tracks() {
        const blanks = self.blanks;
        const adapterTracks = self.MSA
            ? self.MSA.tracks.map(track => {
                const { data } = track;
                return {
                    model: {
                        ...track,
                        data: data ? skipBlanks(blanks, data) : undefined,
                        height: self.rowHeight,
                    },
                    ReactComponent: TextTrack,
                };
            })
            : [];
        const boxTracks = self.boxTracks
            // filter out tracks that are associated with hidden rows
            .filter(track => !!self.rows.some(row => row[0] === track.name))
            .map(track => ({
            model: track,
            ReactComponent: BoxTrack,
        }));
        const annotationTracks = self.annotatedRegions.length > 0
            ? [
                {
                    model: {
                        features: self.annotatedRegions,
                        height: 100,
                        id: 'annotations',
                        name: 'User-created annotations',
                        data: self.annotatedRegions
                            .map(region => {
                            const attrs = region.attributes
                                ? Object.entries(region.attributes)
                                    .map(([k, v]) => `${k}=${v.join(',')}`)
                                    .join(';')
                                : '.';
                            return [
                                'MSA_refcoord',
                                '.',
                                '.',
                                region.start,
                                region.end,
                                '.',
                                '.',
                                '.',
                                attrs,
                            ].join('\t');
                        })
                            .join('\n'),
                    },
                    ReactComponent: BoxTrack,
                },
            ]
            : [];
        return [...adapterTracks, ...boxTracks, ...annotationTracks];
    },
    /**
     * #getter
     */
    get turnedOnTracks() {
        return this.tracks.filter(f => !self.turnedOffTracks.has(f.model.id));
    },
    /**
     * #method
     * returns coordinate in the current relative coordinate scheme
     */
    pxToBp(coord) {
        return Math.floor((coord - self.scrollX) / self.colWidth);
    },
    /**
     * #method
     */
    rowSpecificBpToPx(rowName, position) {
        var _a;
        const { rowNames, rows } = self;
        const index = rowNames.indexOf(rowName);
        const row = rows[index][1];
        const details = self.getRowData(rowName);
        const offset = ((_a = details.range) === null || _a === void 0 ? void 0 : _a.start) || 0;
        const current = position - offset;
        const s = new Set(self.blanks);
        if (current < 0) {
            return 0;
        }
        let j = 0;
        let i = 0;
        for (; i < row.length; i++) {
            if (row[i] !== '-' && j++ === current) {
                break;
            }
        }
        let count = 0;
        for (let k = 0; k < row.length; k++) {
            if (s.has(k) && k < i + 1) {
                count++;
            }
        }
        return i - count;
    },
    /**
     * #method
     */
    globalBpToPx(position) {
        var _a;
        let count = 0;
        const s = new Set(self.blanks);
        for (let k = 0; k < ((_a = self.rows[0]) === null || _a === void 0 ? void 0 : _a[1].length); k++) {
            if (s.has(k) && k < position + 1) {
                count++;
            }
        }
        return position - count;
    },
    /**
     * #method
     */
    relativePxToBp(rowName, position) {
        const { rowNames, rows } = self;
        const index = rowNames.indexOf(rowName);
        if (index !== -1) {
            const row = rows[index][1];
            let k = 0;
            for (let i = 0; i < position; i++) {
                if (row[i] !== '-') {
                    k++;
                }
                else if (k >= position) {
                    break;
                }
            }
            return k;
        }
        return 0;
    },
    /**
     * #method
     */
    getPos(pos) {
        let j = 0;
        for (let i = 0, k = 0; i < pos; i++, j++) {
            while (j === self.blanks[k]) {
                k++;
                j++;
            }
        }
        return j;
    },
}))
    .actions(self => ({
    /**
     * #action
     */
    addAnnotation(start, end, attributes) {
        self.annotatedRegions.push({
            start: self.getPos(start),
            end: self.getPos(end),
            attributes,
        });
    },
    /**
     * #action
     */
    setAnnotationClickBoundaries(left, right) {
        self.annotPos = { left, right };
    },
    /**
     * #action
     */
    clearAnnotationClickBoundaries() {
        self.annotPos = undefined;
    },
    /**
     * #action
     */
    incrementRef() {
        self.nref++;
    },
}))
    .views(self => ({
    /**
     * #getter
     */
    get totalTrackAreaHeight() {
        return sum(self.turnedOnTracks.map(r => r.model.height));
    },
}))
    .actions(self => ({
    afterCreate() {
        addDisposer(self, autorun(async () => {
            const { treeFilehandle } = self;
            if (treeFilehandle) {
                try {
                    self.setTree(await openLocation(treeFilehandle).readFile('utf8'));
                }
                catch (e) {
                    console.error(e);
                    self.setError(e);
                }
            }
        }));
        addDisposer(self, autorun(async () => {
            const { treeMetadataFilehandle } = self;
            if (treeMetadataFilehandle) {
                try {
                    self.setTreeMetadata(await openLocation(treeMetadataFilehandle).readFile('utf8'));
                }
                catch (e) {
                    console.error(e);
                    self.setError(e);
                }
            }
        }));
        addDisposer(self, autorun(async () => {
            const { msaFilehandle } = self;
            if (msaFilehandle) {
                try {
                    self.setMSA(await openLocation(msaFilehandle).readFile('utf8'));
                }
                catch (e) {
                    console.error(e);
                    self.setError(e);
                }
            }
        }));
        addDisposer(self, autorun(async () => {
            if (self.treeWidthMatchesArea) {
                self.setTreeWidth(Math.max(50, self.treeAreaWidth - self.labelsWidth - 20));
            }
        }));
    },
}))
    .postProcessSnapshot(result => {
    const snap = result;
    const { data: { tree, msa, treeMetadata }, ...rest } = snap;
    // remove the MSA/tree data from the tree if the filehandle available in
    // which case it can be reloaded on refresh
    return {
        data: {
            // https://andreasimonecosta.dev/posts/the-shortest-way-to-conditionally-insert-properties-into-an-object-literal/
            ...(!result.treeFilehandle && { tree }),
            ...(!result.msaFilehandle && { msa }),
            ...(!result.treeMetadataFilehandle && { treeMetadata }),
        },
        ...rest,
    };
});
export default model;
//# sourceMappingURL=model.js.map